/**
 * Papel de trabalho da conta — regras de apresentação da Composição de Saldo.
 *
 * Decisões de negócio validadas com o usuário (8º formato aprovado — NÃO
 * reinterpretar):
 *  - A tela chama-se "Papel de trabalho da conta"; "Composição do saldo" é o
 *    nome da ABA, não o título.
 *  - A COMPOSIÇÃO não é razão: cada linha é um item que ficou EM ABERTO e soma
 *    o saldo da conta na data-posição.
 *  - A linha de CONCILIAÇÃO é a alma da tela (razão = X · composição = Y ·
 *    diferença = Z). É o que prova que fechou — o auditor olha isso primeiro.
 *  - Natureza do saldo é a LETRA QUE O BACKEND MANDA (`saldo_razao_natureza` /
 *    `saldo_anterior_natureza`). O sinal do valor NÃO serve para derivá-la: o
 *    backend calcula o saldo NA NATUREZA DA CONTA, então em conta credora
 *    positivo significa "C", não "D" (ver `fmtSaldoComNatureza`).
 *  - `—` (sem valor) é visualmente diferente de `R$ 0,00` (zero real).
 *  - "Pagamento a maior" é SINALIZADO, nunca reclassificado.
 *  - A seção "não identificado" aparece no FIM e SOMA no total — nada é
 *    escondido, senão o total não fecharia com o razão.
 */

import { Natureza, fmtMoney, naturezaDe } from '../balancete';

// Formatação de moeda é a MESMA do Balancete (#709). Reexportamos em vez de
// criar uma terceira cópia — a semântica `—` vs `R$ 0,00` tem que ser única.
// `fmtSaldo` NÃO é reexportado de propósito: ele deriva a letra do sinal, o que
// inverte o D/C em conta credora. Nesta tela use `fmtSaldoComNatureza`.
export { fmtMoney, naturezaDe };
export type { Natureza };

/** Tipo de um item da composição, como o backend classifica. */
export type TipoItem = 'provisao' | 'pagamento_maior' | 'nao_identificado';

/** Uma linha da composição (contrato do endpoint `composicao-saldo`). */
export interface IComposicaoItem {
  numero: number | null;
  data: string | null;
  contrapartida: string | null;
  contrapartida_nome: string | null;
  /** Já vem pronto como "611 Despesa FGTS" — é o que a tela exibe. */
  contrapartida_label: string | null;
  historico: string | null;
  documento: string | null;
  /** Valor AINDA em aberto (já descontadas as baixas parciais casadas). */
  valor_aberto: number;
  /** Valor original do lançamento (p/ "1.000, dos quais 600 em aberto"). */
  valor_original: number;
  tipo: TipoItem;
  /** +1 soma no total (provisão), -1 subtrai (baixa órfã). */
  sinal: number;
  parcial: boolean;
  alerta: string | null;
  /**
   * Dados da NOTA que originou o lançamento — casados por número da NF (do
   * histórico) + valor exato, no backend (`enriquecimento_nota`).
   *
   * `null` quando não casou, e a tela mostra "—". A distinção importa: o
   * contador precisa saber a diferença entre "não achei a nota" e "a nota diz
   * X". Cobertura medida em produção: 63% das provisões — pagamento (PIX,
   * boleto, transferência) não tem nota fiscal, então vazio ali é o correto.
   *
   * OPCIONAIS no tipo de propósito: durante a janela de deploy o front novo
   * fala com o BFF antigo, que ainda não publica os campos. Ausência é tratada
   * como "não casou", nunca como erro.
   */
  nota_numero?: string | null;
  nota_serie?: string | null;
  nota_data?: string | null;
  fornecedor?: string | null;
  fornecedor_cnpj?: string | null;
  /** Código da empresa no Domínio (`external_id`) — a coluna "Filial". */
  filial?: number | null;
  /** Dias entre a data do item e a data-posição: denuncia item velho em aberto. */
  dias?: number | null;
  /**
   * Baixa que compensa um ADIANTAMENTO a fornecedor — não é "pagamento a
   * maior".
   *
   * Quando o cliente paga antes da nota chegar, o valor fica em "adiantamento
   * a fornecedores" (ativo). Ao chegar a nota, o contador debita "fornecedores
   * a pagar" e credita o adiantamento. O motor não casa esse débito com
   * provisão nenhuma, então ele cai no balde de órfãs — mas tem explicação
   * contábil perfeita, e chamá-lo de "não identificado" manda o contador
   * conferir à mão algo que já está certo.
   *
   * O backend rotula (`compensacao_adiantamento.py`) SEM tocar no matching: o
   * item continua com o mesmo tipo, valor e sinal. Medido em produção: 73
   * linhas, R$ 6,2 mi.
   *
   * OPCIONAL: durante a janela de deploy o front pode falar com um BFF que
   * ainda não publica o campo — ausência é tratada como "não é compensação".
   */
  compensacao_adiantamento?: boolean | null;
  /**
   * Quanto deste título JÁ foi pago, e por QUAIS lançamentos.
   *
   * `ligacoes` traz os NÚMEROS dos lançamentos de baixa — o que permite
   * abrir o pagamento no razão. Sem isso a tela sabe que o título foi
   * baixado, mas não por qual pagamento, e o contador não consegue
   * conferir.
   *
   * OPCIONAIS pelo mesmo motivo dos campos de nota: durante a janela de
   * deploy o front pode falar com um BFF que ainda não publica o campo.
   */
  valor_baixado?: number | null;
  ligacoes?: number[] | null;
}

/** Identificação da conta analisada. */
export interface IComposicaoConta {
  codigo: string;
  nome: string | null;
  classificacao: string | null;
  tipo: string | null;
  /** Natureza da CONTA ('C'/'D') — NÃO usar para rotular saldo. */
  natureza: string | null;
  /**
   * A conta SINTÉTICA a que esta analítica pertence — o grupo imediatamente
   * acima dela no plano ("Bancos Conta Movimento" para "Banco do Brasil S/A
   * 111621-5.").
   *
   * É o pai DIRETO, não o topo: num plano de 5 níveis, saber que a conta está
   * no "ATIVO" não ajuda a se localizar.
   *
   * `null` quando a conta é de 1º nível ou o plano da empresa não tem
   * sintéticas — a tela omite a linha em vez de inventar um grupo. Opcional
   * também para a janela de deploy, em que o BFF pode não publicar o campo.
   */
  sintetica_pai?: ISinteticaPai | null;
}

/** Identificação do grupo (conta sintética) exibido acima da analítica. */
export interface ISinteticaPai {
  codigo: string | null;
  nome: string | null;
  classificacao: string | null;
}

/** Payload completo de `GET .../contabil/composicao-saldo`. */
export interface IComposicaoSaldo {
  conta: IComposicaoConta;
  data_posicao: string;
  data_ini: string;
  ano: number;
  ano_anterior: number;
  saldo_razao: number;
  /**
   * Letra D/C do SALDO, calculada pelo backend na natureza da CONTA (já
   * invertida quando o saldo está contrário à natureza). `null` = saldo zero.
   * É ESTA a letra exibida — não derivar do sinal de `saldo_razao`.
   */
  saldo_razao_natureza: string | null;
  saldo_anterior: number | null;
  /** Mesma regra de `saldo_razao_natureza`, para o saldo de abertura. */
  saldo_anterior_natureza: string | null;
  /**
   * `false` é o caminho NORMAL hoje: `extract_lancamentos` começa em 01/2026,
   * então quase nenhuma conta tem saldo de abertura na base. A tela avisa com
   * clareza, sem parecer erro — entrada manual de abertura é outra task.
   */
  abertura_disponivel: boolean;
  /**
   * A carga desta empresa tem LIQUIDAÇÃO, ou só competência?
   *
   * `disponivel: false` significa que nenhuma conta de caixa/banco foi
   * movimentada no período — ou seja, a base tem as provisões mas nenhum
   * pagamento ou recebimento. Nesse caso `conciliado` continua `true`, porque
   * os dois lados da conciliação leem a MESMA base incompleta, e o contador
   * leria "saldo conciliado" sobre dado que não fecha na vida real.
   *
   * OPCIONAL no tipo: durante a janela de deploy o front novo pode receber
   * resposta de um BFF que ainda não publica o campo. Ausência é tratada como
   * "sem aviso" — nunca como erro.
   */
  liquidacao?: ILiquidacao | null;
  itens: IComposicaoItem[];
  /**
   * TOTAIS DA CONTA INTEIRA — não mudam com filtro nem com página.
   * É o que prova que a conta fecha, e é a alma do documento. Um total que
   * acompanhasse o recorte faria a linha de conciliação acusar divergência
   * numa conta que fecha. Nunca some `itens` para exibir isto.
   */
  total_composicao: number;
  total_nao_identificado: number;
  diferenca: number;
  conciliado: boolean;
  /** Estado da paginação do recorte (`itens`), não da conta. */
  paginacao: IPaginacao;
  /** Agregado do subconjunto filtrado — separado do total da conta. */
  filtro: IFiltroResumo;
}

/**
 * Cobertura da carga: a base tem liquidação, ou só competência?
 *
 * O backend decide pela classificação das contas de disponibilidade (`111*`),
 * não pelo nome. O `aviso` vem pronto do servidor de propósito: a explicação é
 * regra de negócio, e duplicá-la no front deixaria as duas versões divergirem.
 */
export interface ILiquidacao {
  disponivel: boolean;
  /** Texto pronto para exibir. `null` quando há liquidação (sem aviso). */
  aviso: string | null;
}

/** O selo de conciliação exibido no rodapé da composição. */
export interface ISeloConciliacao {
  texto: string;
  /** Verde só quando a conciliação pode ser levada a sério. */
  verde: boolean;
  titulo: string;
}

/**
 * Decide o selo de conciliação.
 *
 * O caso que esta função existe para resolver: quando a base não tem
 * liquidação carregada, `conciliado` vem `true` — os dois lados da conta leem
 * a MESMA base incompleta e batem entre si. Pintar isso de verde e escrever
 * "saldo conciliado" faz o contador dar a conta por conferida quando ela não
 * foi. Medido em produção: 365 de 635 empresas nessa situação, R$ 322,7 mi
 * provisionados sem nenhuma baixa possível.
 *
 * Então: sem liquidação, o selo NÃO é verde e não afirma conciliação — diz que
 * ela não pode ser verificada, que é o fato. Não chamamos de "não conciliado"
 * (seria afirmar divergência, e também não sabemos isso).
 */
export const seloConciliacao = (
  conciliado: boolean,
  liquidacao?: ILiquidacao | null,
): ISeloConciliacao => {
  // `liquidacao` ausente = BFF antigo na janela de deploy: mantém o
  // comportamento anterior em vez de acusar um problema que não foi medido.
  if (liquidacao && liquidacao.disponivel === false) {
    return {
      texto: 'conciliação não verificável',
      verde: false,
      titulo:
        'Os dois lados da conciliação leem a mesma base, e ela não tem as ' +
        'liquidações do período — o fechamento aqui não prova que a conta está correta.',
    };
  }
  return conciliado
    ? {
        texto: 'saldo conciliado',
        verde: true,
        titulo: 'A composição fecha com o saldo do razão na data-base',
      }
    : {
        texto: 'saldo não conciliado',
        verde: false,
        titulo: 'A composição não fecha com o razão nesta data-base — conferir antes de concluir',
      };
};

/** Paginação do recorte. `total_itens` é a composição inteira. */
export interface IPaginacao {
  limit: number;
  offset: number;
  /** Itens da composição INTEIRA (sem filtro). */
  total_itens: number;
  /** Itens que o filtro retornou (todas as páginas do filtro). */
  total_filtrado: number;
  paginas: number;
  pagina_atual: number;
  tem_proxima: boolean;
  /** `false` quando o backend devolveu a composição inteira (XLSX/legado). */
  paginado: boolean;
}

/* ------------------------------------------------------------------ */
/* Razão da conta                                                      */
/* ------------------------------------------------------------------ */

/**
 * Uma linha do razão: um lançamento, não um item em aberto.
 *
 * A diferença para `IComposicaoItem` é o que separa as duas abas. A composição
 * mostra o que SOBROU (resultado do matching, com `valor_aberto` e
 * `valor_original`); a razão mostra o que ACONTECEU — um `valor` só, com o
 * `lado` (D/C) dizendo se aumentou ou diminuiu o saldo.
 */
export interface IRazaoItem {
  id: number;
  numero: number | null;
  data: string | null;
  /** 'D' ou 'C': o lado do lançamento NESTA conta. */
  lado: 'D' | 'C';
  valor: number;
  historico: string | null;
  documento: string | null;
  contrapartida: string | null;
  contrapartida_label: string | null;
  /** Lançamento de abertura (saldo transportado), não movimento do período. */
  abertura: boolean;
  /**
   * Saldo da conta APÓS esta linha, partindo do saldo anterior.
   *
   * `null` quando a base não alcança a abertura da conta — aí a tela mostra
   * "—" em toda a coluna. É deliberado: um acumulado que partisse de zero
   * descreveria uma conta que nunca existiu, e o erro seria mudo (as linhas
   * certas, o acumulado deslocado por uma constante).
   */
  saldo_acumulado: number | null;
  filial: number | null;
  /** Colunas da nota, casadas no backend. `null` quando não casou. */
  nota_numero?: string | null;
  nota_serie?: string | null;
  nota_data?: string | null;
  fornecedor?: string | null;
  fornecedor_cnpj?: string | null;
}

/** Paginação da razão: mais simples que a da composição (não tem filtro). */
export interface IRazaoPaginacao {
  limit: number | null;
  offset: number;
  total_itens: number;
  paginas: number;
  paginado: boolean;
}

/** Resposta de `GET /empresas/{id}/contabil/razao`. */
export interface IRazaoConta {
  conta: IComposicaoConta;
  data_ini: string;
  data_fim: string;
  /** Saldo de onde o acumulado parte. `null` sem abertura na base. */
  saldo_anterior: number | null;
  saldo_anterior_natureza: 'D' | 'C' | null;
  /** `false` quando a base não alcança a abertura: o acumulado vem todo null. */
  abertura_disponivel: boolean;
  /** Totais da conta INTEIRA no período — não mudam com a página. */
  total_debito: number;
  total_credito: number;
  /** Movimento na natureza da conta (credora: C−D; devedora: D−C). */
  movimento: number;
  saldo_final: number | null;
  saldo_final_natureza: 'D' | 'C' | null;
  itens: IRazaoItem[];
  paginacao: IRazaoPaginacao;
}

/**
 * Soma do SUBCONJUNTO filtrado. Existe para ser exibida ao LADO do total da
 * conta, nunca no lugar dele — por isso vem num campo próprio e rotulado.
 */
export interface IFiltroResumo {
  ativo: boolean;
  soma: number;
  quantidade: number;
  /** Filtros aplicados em texto (mesma lista que o XLSX filtrado imprime). */
  descricao: string[];
}

/** Operadores do filtro personalizado (contrato do backend). */
export type Operador = 'eq' | 'ne' | 'gt' | 'gte' | 'lt' | 'lte' | 'entre';

/** Rótulos em português — o contador lê "maior que", não "gt". */
export const ROTULO_OPERADOR: Record<Operador, string> = {
  eq: 'igual a',
  ne: 'diferente de',
  gt: 'maior que',
  gte: 'maior ou igual a',
  lt: 'menor que',
  lte: 'menor ou igual a',
  entre: 'entre',
};

export const OPERADORES = Object.keys(ROTULO_OPERADOR) as Operador[];

/**
 * Máximo de itens por página — espelha o TETO do backend (`Query(le=100)`).
 * Não é mais o tamanho padrão da página: é o limite superior do seletor.
 * Pedir mais que isto devolve 422 (`?limit=5000` reprova na validação).
 */
export const ITENS_POR_PAGINA = 100;

/**
 * Opções do seletor "linhas por página". O teto é `ITENS_POR_PAGINA` — oferecer
 * um valor acima faria a tela pedir ao backend algo que ele recusa com 422.
 */
export const OPCOES_ITENS_POR_PAGINA = [25, 50, 100] as const;

/** Nível de amostragem: quantas linhas a grade mostra por página. */
export type ItensPorPagina = (typeof OPCOES_ITENS_POR_PAGINA)[number];

/**
 * Padrão pedido pelo usuário: a grade ABRE em 25 linhas ("padrão começa em 25
 * linhas, podendo escolher entre 25, 50 e 100"). Amostragem menor carrega mais
 * rápido; quem quiser varrer a conta sobe para 50/100.
 */
export const ITENS_POR_PAGINA_PADRAO: ItensPorPagina = 25;

/**
 * Normaliza um valor arbitrário (vindo de `<select>`, URL ou localStorage) numa
 * opção válida. Valor fora da lista cai no padrão em vez de virar `limit`
 * inválido: um `NaN` ou um `5000` vazado até a query key viraria 422.
 */
export const normalizarItensPorPagina = (v: unknown): ItensPorPagina => {
  const n = Number(v);
  return (OPCOES_ITENS_POR_PAGINA as readonly number[]).includes(n)
    ? (n as ItensPorPagina)
    : ITENS_POR_PAGINA_PADRAO;
};

/** Estado dos filtros na tela (tudo string: vem de inputs). */
export interface IFiltrosUI {
  historico: string;
  contrapartida: string;
  conta_contrapartida: string;
  documento: string;
  tipo: string;
  periodo_de: string;
  periodo_ate: string;
  valor_op: Operador | '';
  valor: string;
  valor_ate: string;
  data_op: Operador | '';
  data_filtro: string;
  data_filtro_ate: string;
}

export const FILTROS_VAZIOS: IFiltrosUI = {
  historico: '',
  contrapartida: '',
  conta_contrapartida: '',
  documento: '',
  tipo: '',
  periodo_de: '',
  periodo_ate: '',
  valor_op: '',
  valor: '',
  valor_ate: '',
  data_op: '',
  data_filtro: '',
  data_filtro_ate: '',
};

/**
 * Converte o estado da tela nos query params do backend.
 *
 * Campos vazios são OMITIDOS (não viram `?historico=`): mandar vazio faria o
 * backend tratar como filtro ativo e a tela mostraria o bloco "soma do filtro"
 * sem filtro nenhum.
 *
 * Operador sem valor também é omitido — "maior que (nada)" não é filtro. E
 * `entre` só é enviado com os DOIS lados preenchidos, senão o backend responde
 * 422 e a tela quebraria enquanto o usuário ainda está digitando.
 */
export const filtrosParaParams = (f: IFiltrosUI): Record<string, string> => {
  const p: Record<string, string> = {};
  const texto: (keyof IFiltrosUI)[] = [
    'historico',
    'contrapartida',
    'conta_contrapartida',
    'documento',
    'tipo',
    'periodo_de',
    'periodo_ate',
  ];
  texto.forEach(k => {
    const v = String(f[k] ?? '').trim();
    if (v) p[k] = v;
  });

  if (f.valor_op && f.valor.trim()) {
    if (f.valor_op !== 'entre' || f.valor_ate.trim()) {
      p.valor_op = f.valor_op;
      p.valor = f.valor.trim();
      if (f.valor_op === 'entre') p.valor_ate = f.valor_ate.trim();
    }
  }
  if (f.data_op && f.data_filtro.trim()) {
    if (f.data_op !== 'entre' || f.data_filtro_ate.trim()) {
      p.data_op = f.data_op;
      p.data_filtro = f.data_filtro.trim();
      if (f.data_op === 'entre') p.data_filtro_ate = f.data_filtro_ate.trim();
    }
  }
  return p;
};

/**
 * Lê filtros vindos da URL (`?historico=juros&valor_op=gt&valor=1000`).
 *
 * Existe para o caso de a tela ser aberta por link/atalho já filtrada: sem
 * isto o recorte chegaria à grade sem nenhum campo preenchido, e o usuário
 * veria "300 de 22.095 itens" sem nada que explicasse o corte. Quando algum
 * filtro vem por aqui, a tela abre com o bloco EXPANDIDO.
 *
 * Só reconhece as chaves do contrato — query string é entrada externa, e
 * aceitar chave arbitrária encheria `IFiltrosUI` de campo inexistente.
 */
export const filtrosDaUrl = (params: URLSearchParams): IFiltrosUI => {
  const bruto: Record<string, string> = { ...FILTROS_VAZIOS };
  Object.keys(FILTROS_VAZIOS).forEach(k => {
    const v = params.get(k);
    if (v) bruto[k] = v;
  });
  // O objeto nasce de FILTROS_VAZIOS e só recebe chaves dele: o shape continua
  // sendo o de IFiltrosUI, com os operadores restritos aos valores do contrato.
  return bruto as unknown as IFiltrosUI;
};

/**
 * Quantos filtros estão preenchidos — alimenta o badge "N filtros ativos".
 *
 * Um filtro de valor viaja em 2 ou 3 params (`valor_op` + `valor` + às vezes
 * `valor_ate`), mas para o usuário é UM filtro. Contar params faria "valor
 * entre 100 e 500" aparecer como "3 filtros ativos", que é simplesmente falso.
 * Por isso os params satélite são descartados e a contagem fica na intenção.
 */
const PARAMS_SATELITE = ['valor', 'valor_ate', 'data_filtro', 'data_filtro_ate'];

export const contaFiltrosAtivos = (f: IFiltrosUI): number =>
  Object.keys(filtrosParaParams(f)).filter(k => !PARAMS_SATELITE.includes(k)).length;

/** Nome legível de cada campo — o contador lê "Histórico", não "historico". */
const ROTULO_CAMPO_FILTRO: Partial<Record<keyof IFiltrosUI, string>> = {
  historico: 'Histórico',
  contrapartida: 'Contrapartida',
  conta_contrapartida: 'Conta',
  documento: 'Documento',
  tipo: 'Tipo',
  periodo_de: 'Período de',
  periodo_ate: 'Período até',
};

/** Rótulo do `tipo`, que na tela é um select e não o slug do backend. */
const ROTULO_TIPO: Record<string, string> = {
  provisao: 'em aberto',
  pagamento_maior: 'pagamento a maior',
  nao_identificado: 'não identificado',
};

/**
 * Resumo em UMA LINHA dos filtros aplicados ("Histórico: juros · Valor maior
 * que 1000").
 *
 * Existe para o bloco de filtros RECOLHIDO: com os campos escondidos, o badge
 * "N filtros ativos" diz quantos mas não QUAIS, e a grade mostrando 300 de
 * 22.095 itens sem dizer por quê é indistinguível de dado faltando. Este texto
 * é o que mantém o recorte visível quando o bloco está fechado.
 *
 * Só descreve o que de fato virou filtro (reusa `filtrosParaParams`), então
 * operador incompleto e campo em branco nunca aparecem aqui.
 */
export const resumoFiltrosAplicados = (f: IFiltrosUI): string => {
  const enviados = filtrosParaParams(f);
  const partes: string[] = [];

  (Object.keys(ROTULO_CAMPO_FILTRO) as (keyof IFiltrosUI)[]).forEach(k => {
    const v = enviados[k as string];
    if (!v) return;
    const rotulo = ROTULO_CAMPO_FILTRO[k];
    partes.push(`${rotulo}: ${k === 'tipo' ? (ROTULO_TIPO[v] ?? v) : v}`);
  });

  if (enviados.valor_op) {
    const op = ROTULO_OPERADOR[enviados.valor_op as Operador] ?? enviados.valor_op;
    partes.push(
      enviados.valor_op === 'entre'
        ? `Valor entre ${enviados.valor} e ${enviados.valor_ate}`
        : `Valor ${op} ${enviados.valor}`,
    );
  }
  if (enviados.data_op) {
    const op = ROTULO_OPERADOR[enviados.data_op as Operador] ?? enviados.data_op;
    partes.push(
      enviados.data_op === 'entre'
        ? `Data entre ${fmtData(enviados.data_filtro)} e ${fmtData(enviados.data_filtro_ate)}`
        : `Data ${op} ${fmtData(enviados.data_filtro)}`,
    );
  }

  return partes.join(' · ');
};

/** Página (1-based) a partir do offset. */
export const paginaDoOffset = (offset: number, limit: number): number =>
  Math.floor(offset / Math.max(1, limit)) + 1;

/**
 * Saldo formatado com a natureza QUE O BACKEND MANDA (`R$ 253,02 D`).
 *
 * NÃO derivar do sinal. O backend devolve `saldo_razao` já NA NATUREZA DA
 * CONTA (conta credora: C − D; devedora: D − C), então em conta de passivo/PL/
 * receita um valor POSITIVO é credor. Derivar a letra do sinal invertia o D/C
 * em toda conta credora — que é a maioria das contas onde esta tela é usada
 * (comprovado com dado real: 1186/186 `-253,02` saía "C" devendo ser "D";
 * 856/201 `623.939,30` saía "D" devendo ser "C").
 *
 * `saldo_razao_natureza` já trata o caso "conta credora com saldo devedor":
 * o backend inverte a letra quando o saldo é negativo. Saldo zero vem sem
 * letra (`null`) — `R$ 0,00` não é nem devedor nem credor.
 *
 * O `fallback` no sinal só entra quando o campo vier ausente/`null` COM valor
 * diferente de zero (contrato antigo / payload incompleto); é degradação, não
 * a regra.
 *
 * O valor exibido é o ABSOLUTO + a letra, como o contador lê balancete.
 */
export const fmtSaldoComNatureza = (
  saldo: number | null | undefined,
  natureza: string | null | undefined,
): string => {
  if (saldo == null) return '—';
  const base = fmtMoney(Math.abs(saldo));
  // Zero real não recebe letra, mesmo que o backend mande alguma.
  if (saldo === 0) return base;
  const letra = natureza === 'D' || natureza === 'C' ? natureza : naturezaDe(saldo);
  return letra ? `${base} ${letra}` : base;
};

/** Data ISO (`YYYY-MM-DD`) no formato brasileiro, sem passar por `Date` (fuso). */
export const fmtData = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : iso;
};

/**
 * Célula vazia vira "—", nunca string vazia nem "null".
 *
 * É o traço que o usuário pediu, e ele carrega significado: distingue "não há
 * dado" de "o dado é zero/em branco". Numa tabela de 13 colunas onde 5 só
 * casam em 63% dos casos, a diferença é o que impede o contador de achar que
 * a nota não existe quando ela só não foi encontrada.
 */
export const ouTraco = (v: string | number | null | undefined): string => {
  if (v === null || v === undefined) return '—';
  const s = String(v).trim();
  return s === '' ? '—' : s;
};

/** CNPJ em 00.000.000/0000-00; devolve cru se não tiver 14 dígitos. */
export const fmtCnpj = (cnpj: string | null | undefined): string => {
  if (!cnpj) return '—';
  const d = String(cnpj).replace(/\D/g, '');
  if (d.length !== 14) return ouTraco(cnpj);
  return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
};

/**
 * "50" para dias em aberto. Zero é valor legítimo (item do próprio dia), então
 * só `null`/`undefined` viram "—" — `ouTraco` sozinho trataria 0 como vazio se
 * fosse string.
 */
export const fmtDias = (dias: number | null | undefined): string =>
  dias === null || dias === undefined ? '—' : String(dias);

/** Só as linhas normais (em aberto) — a seção "não identificado" vai à parte. */
export const itensEmAberto = (itens: IComposicaoItem[]): IComposicaoItem[] =>
  itens.filter(i => i.tipo === 'provisao');

/**
 * Itens que NÃO casaram: pagamento a maior e não identificado.
 * Aparecem no FIM da tela, em seção própria, e SOMAM no total — esconder
 * quebraria a conciliação.
 */
export const itensNaoIdentificados = (itens: IComposicaoItem[]): IComposicaoItem[] =>
  itens.filter(i => i.tipo !== 'provisao');

/**
 * Baixas órfãs que são COMPENSAÇÃO de adiantamento — têm explicação contábil.
 *
 * Saem do bloco "não identificado" para um bloco próprio: misturar as duas
 * coisas faz o contador conferir à mão 73 linhas que já estão certas. Elas
 * continuam somando no total exatamente como antes — o backend não mudou o
 * tipo nem o valor delas, só o rótulo.
 */
export const itensCompensacao = (itens: IComposicaoItem[]): IComposicaoItem[] =>
  itens.filter(i => i.tipo !== 'provisao' && i.compensacao_adiantamento === true);

/**
 * As órfãs que continuam SEM explicação — o "não identificado" de verdade.
 *
 * `=== true` de propósito: campo ausente (BFF antigo na janela de deploy) ou
 * `null` mantém o item aqui, que é o comportamento de antes desta frente.
 */
export const itensNaoIdentificadosReais = (itens: IComposicaoItem[]): IComposicaoItem[] =>
  itens.filter(i => i.tipo !== 'provisao' && i.compensacao_adiantamento !== true);

/** Valor com que o item entra no total (baixa órfã entra negativa). */
export const valorComSinal = (item: IComposicaoItem): number =>
  (item.sinal < 0 ? -1 : 1) * Math.abs(item.valor_aberto ?? 0);

/**
 * Período do exercício anterior, para o link "ver <ano-1> ›".
 * Ano civil completo — o mesmo recorte que o Balancete usa.
 */
export const periodoDoAno = (ano: number) => ({
  data_ini: `${ano}-01-01`,
  data_fim: `${ano}-12-31`,
});

/** Um lado do painel de composição: a receber (ativo) ou a pagar (passivo). */
export interface ILadoComposicao {
  /** `receber` = contas de ativo, `pagar` = passivo, `outro` = o resto. */
  lado: 'receber' | 'pagar' | 'outro';
  contas: number;
  /**
   * O saldo do razão das contas deste lado.
   *
   * `null` quando o exercício pedido não tem fotografia do lote (ano anterior,
   * calculado na hora): o saldo vive no resumo, que só guarda o exercício
   * corrente. Zero afirmaria "o razão fecha em zero" — bem diferente de "não
   * há saldo medido para esta data".
   */
  saldo_razao: number | null;
  /** O título gerado no período: venda a prazo, nota de entrada. */
  provisionado: number;
  em_aberto: number;
  /** Quanto do título já foi pago E ligado a um pagamento identificado. */
  conciliado: number;
  sem_par: number;
  /** A parte do `sem_par` que NÃO TEM par: cartão, transferência própria. */
  sem_par_natureza: number;
  sem_par_real: number;
  titulos: number;
  titulos_ligados: number;
  /** `null` = não há base para medir. NUNCA confundir com 0%. */
  pct_casado: number | null;
}

/**
 * Indicadores da composição da empresa, quebrados por lado.
 *
 * `data_posicao: null` = o lote nunca rodou para esta empresa; a tela mostra
 * o `aviso` em vez de zeros, que afirmariam 'nada conciliado'.
 */
/** Uma conta da composição, para a lista do bloco "Ver detalhes". */
export interface IContaDetalhe {
  conta: string;
  conta_nome?: string | null;
  classificacao?: string | null;
  provisionado: number;
  em_aberto: number;
  conciliado: number;
  sem_par: number;
  sem_par_natureza: number;
  sem_par_real: number;
  titulos: number;
  titulos_ligados: number;
  pct_casado: number | null;
}

export interface IComposicaoLados {
  data_posicao: string | null;
  aviso?: string;
  lados?: ILadoComposicao[];
  /** As contas que compõem, para a lista do "Ver detalhes". */
  contas_detalhe?: IContaDetalhe[];
  /** A SOMA dos lados — nunca uma segunda consulta, para não divergir. */
  total?: Omit<ILadoComposicao, 'lado'>;
  /** O ano contábil a que estes números se referem. */
  exercicio?: number;
  /**
   * De onde o número veio.
   *
   * `lote` = fotografia do job das 03:00 (o exercício corrente, instantâneo).
   * `sob_demanda` = calculado na hora, porque o lote só grava o exercício
   * corrente — medido em produção, não existe fotografia de 2025.
   */
  origem?: 'lote' | 'sob_demanda';
  calculado_agora?: boolean;
  /** `true` = faltou conta ou estourou o tempo; a tela precisa dizer. */
  parcial?: boolean;
}

/**
 * Um lançamento buscado pelo NÚMERO (rota `/contabil/lancamentos`).
 *
 * Diferente de `IRazaoItem`: aquele vem da página do razão e depende dela;
 * este é buscado pelos números que `ligacoes` guarda, SEM filtro de data.
 * Medido na NF 22401: 9 de 13 pagamentos sumiam da tela por paginação, e
 * nenhum deles era de outro exercício.
 */
export interface ILancamentoLigado {
  id: number;
  numero: number | null;
  data: string | null;
  valor: number;
  historico: string | null;
  documento: string | null;
  conta_debito: string | null;
  conta_credito: string | null;
  /** `D`/`C` em relação à conta analisada; `null` quando não há conta. */
  lado: 'D' | 'C' | null;
  contrapartida: string | null;
  contrapartida_label: string | null;
  /** `true` = fora do período da tela. A janela MARCA, nunca filtra. */
  fora_da_janela: boolean;
}

export interface ILancamentosLigados {
  itens: ILancamentoLigado[];
  pedidos?: number;
  encontrados?: number;
}

/**
 * Onde um lançamento cai no razão da conta.
 *
 * `pos: null` = não pertence à conta no período; a tela avisa em vez de levar
 * o usuário a uma página onde não há nada.
 */
export interface IPosicaoRazao {
  pos: number | null;
  pagina: number | null;
  offset: number | null;
}
